import React from 'react';

// Import config and utils
import { useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_SHORT_TEXT,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  composeValidators,
  emailFormatValid,
  required,
  nonEmptyArray,
  validateInteger,
  validateYoutubeURL,
} from '../../util/validators';
import { checkOrgNumber, isSingleLineField, isWebAddress } from '../../util/userFieldValues';
import { startsOptionGroup } from '../../util/fieldHelpers';
// Import shared components
import {
  FieldCheckboxGroup,
  FieldSelect,
  FieldTextInput,
  FieldBoolean,
  FieldLabel,
} from '../../components';
// Import modules from this directory
import css from './CustomExtendedDataField.module.css';

// The line between two groups of options in a select: a disabled option, as
// <hr> inside <select> isn't valid in React 18.
const OPTION_GROUP_DIVIDER = '──────';

const createFilterOptions = (options) =>
  options.map((o) => ({ key: `${o.option}`, label: o.label, group: o.group }));

// Every custom listing field's label flows through here, so decorating it
// once covers brand / colour / material / size / condition / fit in one go.
// Required-ness is read from `saveConfig.isRequired` — the same source the
// validators below use, and the same one Sharetribe enforces at runtime — so
// the marker can never disagree with what actually blocks publishing.
// `showRequiredMarker` is opt-in on purpose. This component also backs the
// signup, confirm-signup, profile-settings, checkout, request-quote and
// make-offer forms; decorating unconditionally leaked "(valgfritt)" into all
// of them. Only the listing form passes it.
//
// Required-ness is read from `saveConfig.isRequired` — the same source the
// validators below use, and what Sharetribe enforces at runtime — so the
// marker can never disagree with what actually blocks publishing.
const getLabel = (fieldConfig, showRequiredMarker = false) => {
  const text = fieldConfig?.saveConfig?.label || fieldConfig?.label;
  if (!text || !showRequiredMarker) {
    return text;
  }
  return <FieldLabel isRequired={fieldConfig?.saveConfig?.isRequired === true}>{text}</FieldLabel>;
};

const CustomFieldEnum = (props) => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, showRequiredMarker } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const filterOptions = createFilterOptions(enumOptions);
  // A value the list doesn't offer counts as no choice: a waist size kept
  // after the seller switched from Jeans to Kjoler shows as unchosen, so it
  // has to be chosen again.
  const isRequiredValid = required(requiredMessage || defaultRequiredMessage);
  const validateMaybe = isRequired
    ? {
        validate: (value) =>
          isRequiredValid(filterOptions.some((o) => o.key === `${value}`) ? value : null),
      }
    : {};
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });

  const label = getLabel(fieldConfig, showRequiredMarker);

  return filterOptions ? (
    <FieldSelect
      className={css.customField}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={label}
      helpText={fieldConfig?.helpText}
      {...validateMaybe}
    >
      <option disabled value="">
        {placeholder}
      </option>
      {filterOptions.map((optionConfig, index) => {
        const key = optionConfig.key;
        // A group of its own (the waist sizes) starts after a divider line.
        const divider = startsOptionGroup(filterOptions, index) ? (
          <option key={`divider-${optionConfig.group}`} disabled value={`--${optionConfig.group}`}>
            {OPTION_GROUP_DIVIDER}
          </option>
        ) : null;
        return (
          <React.Fragment key={key}>
            {divider}
            <option value={key}>{optionConfig.label}</option>
          </React.Fragment>
        );
      })}
    </FieldSelect>
  ) : null;
};

const CustomFieldMultiEnum = (props) => {
  const { name, fieldConfig, defaultRequiredMessage, formId, showRequiredMarker } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig, showRequiredMarker);
  const validateMaybe = isRequired
    ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
    : {};

  // Long option lists (more than 5) shift into a 2-column grid so the
  // form doesn't grow into a tall vertical scroll. Short lists stay
  // single-column so they read as a tight checklist.
  const options = createFilterOptions(enumOptions);
  return enumOptions ? (
    <FieldCheckboxGroup
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      helpText={fieldConfig?.helpText}
      options={options}
      twoColumns={options.length > 5}
      {...validateMaybe}
    />
  ) : null;
};

const CustomFieldShortText = (props) => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, showRequiredMarker } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig, showRequiredMarker);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      maxLength={70}
      label={label}
      helpText={fieldConfig?.helpText}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldText = (props) => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, showRequiredMarker } = props;
  const saveConfig = fieldConfig?.saveConfig || {};
  const { placeholderMessage, isRequired, requiredMessage, validateAs } = saveConfig;
  const label = getLabel(fieldConfig, showRequiredMarker);
  const message = (id) => intl.formatMessage({ id: `CustomExtendedDataField.${id}` });

  // A single-line field (see util/userFieldValues.js) is an <input> with the
  // attributes its config gives; any other text field is a text area. A
  // format check only runs on a filled-in value, so an optional field can
  // be left empty.
  const isSingleLine = isSingleLineField(fieldConfig);
  const formatCheck = {
    email: (value) => emailFormatValid(message('invalidEmail'))(value),
    postcode: (value) =>
      /^\d{4}$/.test(`${value}`.trim()) ? undefined : message('invalidPostcode'),
    orgNumber: (value) => {
      const result = checkOrgNumber(value);
      return result === 'ok'
        ? undefined
        : message(result === 'length' ? 'invalidOrgNumber' : 'invalidOrgNumberCheck');
    },
    url: (value) => (isWebAddress(value) ? undefined : message('invalidUrl')),
  }[validateAs];
  const requiredMaybe = isRequired ? required(requiredMessage || defaultRequiredMessage) : null;
  const formatMaybe = formatCheck ? (value) => (value ? formatCheck(value) : undefined) : null;
  const validators = [requiredMaybe, formatMaybe].filter(Boolean);
  const validateMaybe = validators.length > 0 ? { validate: composeValidators(...validators) } : {};
  const placeholder = placeholderMessage || message('placeholderText');

  // A web address is a text input with the URL keyboard, not type="url": the
  // browser would refuse "bedriften.no" without https:// and block the whole
  // form, while our own check accepts it and adds https:// on save.
  const isUrl = validateAs === 'url';
  const inputType = !isSingleLine ? 'textarea' : validateAs === 'email' ? 'email' : 'text';
  const inputMode = saveConfig.inputMode || (isUrl ? 'url' : null);
  const inputAttributes = isSingleLine
    ? {
        ...(saveConfig.maxLength ? { maxLength: saveConfig.maxLength } : {}),
        ...(saveConfig.autoComplete ? { autoComplete: saveConfig.autoComplete } : {}),
        ...(inputMode ? { inputMode } : {}),
      }
    : {};

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type={inputType}
      {...inputAttributes}
      label={label}
      helpText={fieldConfig?.helpText}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldLong = (props) => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, showRequiredMarker } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig, showRequiredMarker);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      helpText={fieldConfig?.helpText}
      parse={(value) => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }}
      label={label}
      placeholder={placeholder}
      validate={(value) => validate(value, minimum, maximum)}
      onWheel={(e) => {
        // fix: number input should not change value on scroll
        if (e.target === document.activeElement) {
          // Prevent the input value change, because we prefer page scrolling
          e.target.blur();

          // Refocus immediately, on the next tick (after the current function is done)
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
    />
  );
};

const CustomFieldBoolean = (props) => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, showRequiredMarker } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig, showRequiredMarker);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      helpText={fieldConfig?.helpText}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldYoutube = (props) => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, showRequiredMarker } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig, showRequiredMarker);
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderYoutubeVideoURL' });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = (value) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      helpText={fieldConfig?.helpText}
      placeholder={placeholder}
      validate={(value) => validate(value)}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = (props) => {
  const intl = useIntl();
  const { enumOptions = [], schemaType } = props?.fieldConfig || {};
  const defaultRequiredMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.required',
  });
  const renderFieldComponent = (FieldComponent, props) => (
    <FieldComponent {...props} defaultRequiredMessage={defaultRequiredMessage} intl={intl} />
  );

  return schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
      ? renderFieldComponent(CustomFieldMultiEnum, props)
      : schemaType === SCHEMA_TYPE_SHORT_TEXT
        ? renderFieldComponent(CustomFieldShortText, props)
        : schemaType === SCHEMA_TYPE_TEXT
          ? renderFieldComponent(CustomFieldText, props)
          : schemaType === SCHEMA_TYPE_LONG
            ? renderFieldComponent(CustomFieldLong, props)
            : schemaType === SCHEMA_TYPE_BOOLEAN
              ? renderFieldComponent(CustomFieldBoolean, props)
              : schemaType === SCHEMA_TYPE_YOUTUBE
                ? renderFieldComponent(CustomFieldYoutube, props)
                : null;
};

export default CustomExtendedDataField;
